import Icon from '@/components/Icon/Icon';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { isNumber } from '@/utils/typeGuards';
type PaginationProps = Readonly<{
  /** 件数を表示したい場合に渡す。渡すと「○○ 件」と表示される */
  totalItems?: number;
  pageSize: number;
  currentPage: number;
  maxPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** DataTablePagination 等から独自の翻訳テキストを渡すためのオーバーライド */
  labelOverrides?: {
    totalCount?: string;
    perPage?: string;
  };
}>;
export function Pagination({
  totalItems,
  pageSize,
  currentPage,
  maxPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  labelOverrides
}: PaginationProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.pagination'
  });
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < maxPage;
  const displayMaxPage = maxPage === 0 ? 1 : maxPage;
  const totalCountText = labelOverrides?.totalCount ?? t('items', {
    count: totalItems
  });
  const perPageText = labelOverrides?.perPage ?? t('itemsPerPage');
  return <div className="flex items-center justify-between max-md:px-7">
      {/* 件数 (デスクトップのみ) */}
      {isNumber(totalItems) && <div className="flex max-md:hidden flex-initial items-center pl-3 text-sm text-neutral-muted-on-fill">
          {totalCountText}
        </div>}

      {/* ページ情報 (モバイル) */}
      <div className="hidden max-md:flex items-center text-sm font-semibold text-neutral-muted-on-fill">
        {t('pageOrder', {
        page: currentPage,
        total: displayMaxPage
      })}
      </div>

      <div className="flex items-center gap-6 px-2 ml-auto max-md:ml-0 lg:gap-8">
        {/* 表示件数 (デスクトップのみ) */}
        <div className="flex max-md:hidden items-center space-x-2">
          <p className="text-sm font-semibold">{perPageText}</p>
          <Select value={`${pageSize}`} onValueChange={value => {
          onPageSizeChange(Number(value));
        }}>
            <SelectTrigger className="h-8 w-fit bg-neutral-fill">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map(size => <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* ページ情報 (デスクトップ) */}
        <div className="flex max-md:hidden items-center text-sm font-semibold">
          {t('pageOrder', {
          page: currentPage,
          total: displayMaxPage
        })}
        </div>

        {/* ページネーションボタン */}
        <div className="flex items-center space-x-2">
          <Button variant="neutral" className="h-8 w-8 p-0" onClick={() => onPageChange(1)} disabled={!canGoPrevious}>
            <span className="sr-only">{t('goToFirstPage')}</span>
            <Icon iconName="ChevronsLeft" />
          </Button>
          <Button variant="neutral" className="h-8 w-8 p-0" onClick={() => onPageChange(currentPage - 1)} disabled={!canGoPrevious}>
            <span className="sr-only">{t('goToPreviousPage')}</span>
            <Icon iconName="ChevronLeft" />
          </Button>
          <Button variant="neutral" className="h-8 w-8 p-0" onClick={() => onPageChange(currentPage + 1)} disabled={!canGoNext}>
            <span className="sr-only">{t('goToNextPage')}</span>
            <Icon iconName="ChevronRight" />
          </Button>
          <Button variant="neutral" className="h-8 w-8 p-0" onClick={() => onPageChange(maxPage)} disabled={!canGoNext}>
            <span className="sr-only">{t('goToLastPage')}</span>
            <Icon iconName="ChevronsRight" />
          </Button>
        </div>
      </div>
    </div>;
}